import { z } from 'zod';

// ---- Line classifications -----------------------------------------------

export const LineClassificationDto = z.object({
  classificationId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  // Inactive classifications are retained for historical reporting; only the
  // admin pane surfaces them, and new-selection pickers filter them out.
  activeFlag: z.boolean(),
  // The classification a NEW task starts on. At most one row is true, and zero is
  // possible (a fresh database runs migrations before the seed), so readers fall
  // back to the first active row rather than assuming one exists. Never infer the
  // default from list position — that is the bug this field replaced.
  isDefault: z.boolean(),
  createdAt: z.string(),
});
export type LineClassificationDto = z.infer<typeof LineClassificationDto>;

export const CreateLineClassificationRequest = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(1000).optional(),
});
export type CreateLineClassificationRequest = z.infer<typeof CreateLineClassificationRequest>;

// Edit, deactivate, and/or promote a classification to default. All fields
// optional (partial patch). Any classification may be renamed; the server rejects
// deactivating the last active one or the current default, and promoting an
// inactive one (tasks.classification_id is NOT NULL, and a new task must not
// preselect a retired type).
//
// `isDefault` accepts only `true`. Sending `false` is a 409, not a no-op: the
// default moves by being set on another row, so the catalog is never left
// without one. Creation does not accept the field at all — the default moves
// only through an explicit, audited patch.
export const UpdateLineClassificationRequest = z
  .object({
    name: z.string().min(1).max(255),
    description: z.string().max(1000).nullable(),
    activeFlag: z.boolean(),
    isDefault: z.boolean(),
  })
  .partial();
export type UpdateLineClassificationRequest = z.infer<typeof UpdateLineClassificationRequest>;

// ---- Projects ---------------------------------------------------------------

// A project lead (project-level role, distinct from the team-level approver role).
// `isApprover` is true when this lead is also an approver of the project's owning
// team — surfaced as a tag in the UI; it does not change the lead's project rights.
export const ProjectLeadDto = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  isApprover: z.boolean(),
});
export type ProjectLeadDto = z.infer<typeof ProjectLeadDto>;

// A team on the project's palette (Model B, slice S-B): teams that CAN be
// assigned to this project's tasks. `memberCount` is the team's total membership,
// shown as "· N" on the chip. Rendered as sidebar pills and Project-setup chips.
export const ProjectTeamSummaryDto = z.object({
  teamId: z.string().uuid(),
  name: z.string(),
  color: z.string(),
  memberCount: z.number(),
});
export type ProjectTeamSummaryDto = z.infer<typeof ProjectTeamSummaryDto>;

export const ProjectDto = z.object({
  projectId: z.string().uuid(),
  teamId: z.string().uuid(),
  leads: z.array(ProjectLeadDto),
  teams: z.array(ProjectTeamSummaryDto),
  name: z.string(),
  code: z.string(),
  description: z.string().nullable(),
  lockedAt: z.string().nullable(),
  // Non-null when the project is archived: it seeds no new timesheet rows and
  // drops out of the default project views.
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  taskCount: z.number(),
});
export type ProjectDto = z.infer<typeof ProjectDto>;

// What archiving this project would cut. Purely informational — it drives the
// confirm dialog's copy and has no `canArchive` gate, because archive is always
// allowed.
//
// `affectedTeams` is here because a project can be staffed via
// project_team_memberships by teams OTHER than its owning team, and approvers of
// those teams have no authorization path into archive or restore. The archiving
// manager must at least see whose staffing they are about to cut.
export const ProjectArchiveImpact = z.object({
  taskCount: z.number(),
  assignedUserCount: z.number(),
  loggedMinutes: z.number(),
  pendingApprovalCount: z.number(),
  /**
   * Teams that genuinely lose staffing to an archive — or get it back on a
   * restore. Direction follows the project's current state, because one dialog
   * serves both.
   *
   * `affectedMemberCount` is the number of that team's members holding a
   * relevant assignment on THIS project, not the team's headcount. A team of
   * twenty with one person on the project loses one person, and saying "20"
   * in a confirmation is just wrong.
   */
  affectedTeams: z.array(
    z.object({
      teamId: z.string().uuid(),
      name: z.string(),
      affectedMemberCount: z.number(),
    }),
  ),
});
export type ProjectArchiveImpact = z.infer<typeof ProjectArchiveImpact>;

export const AddProjectLeadRequest = z.object({
  userId: z.string().uuid(),
});
export type AddProjectLeadRequest = z.infer<typeof AddProjectLeadRequest>;

export const CreateProjectRequest = z.object({
  name: z.string().min(1).max(255),
  code: z.string().min(1).max(64),
  description: z.string().max(2000).optional(),
  teamId: z.string().uuid(),
});
export type CreateProjectRequest = z.infer<typeof CreateProjectRequest>;

// Unknown keys are stripped, not rejected — deliberately. Projects no longer
// carry a workflow status, and a stale client (an SPA tab cached from before the
// removal) still sends `status` alongside its real edits; stripping lets those
// edits land. A body with no editable field left is rejected by the handler.
export const PatchProjectRequest = z
  .object({
    name: z.string().min(1).max(255),
    code: z.string().min(1).max(64),
    description: z.string().max(2000).nullable(),
  })
  .partial();
export type PatchProjectRequest = z.infer<typeof PatchProjectRequest>;

export const LockProjectRequest = z.object({
  lockedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type LockProjectRequest = z.infer<typeof LockProjectRequest>;

// `releaseCode` is required rather than defaulted: the safe answer depends on
// whether hours were ever logged (freeing the code rewrites the code shown on
// every historical QuickSight row for the project), so the dialog decides it
// from the archive impact and always sends it explicitly.
//
// `observedLoggedMinutes` is that decision's premise, sent back so the server
// can check it still holds. The dialog defaults the box ON only when the impact
// reported zero logged minutes, and shows no caution in that case — so if an
// hour lands between the impact loading and confirm being pressed, the manager
// frees the code having been told the opposite of what is now true. Required for
// the same reason `releaseCode` is: the dialog cannot enable confirm until the
// impact has loaded, so it always has a real value to send.
export const ArchiveProjectRequest = z.object({
  releaseCode: z.boolean(),
  observedLoggedMinutes: z.number().int().nonnegative(),
});
export type ArchiveProjectRequest = z.infer<typeof ArchiveProjectRequest>;

// ---- Tasks ------------------------------------------------------------------

export const TaskDto = z.object({
  taskId: z.string().uuid(),
  projectId: z.string().uuid(),
  classificationId: z.string().uuid(),
  name: z.string(),
  activeFlag: z.boolean(),
  lockedAt: z.string().nullable(),
  createdAt: z.string(),
  teamIds: z.array(z.string().uuid()),
  assignees: z.array(
    z.object({
      assignmentId: z.string().uuid(),
      userId: z.string().uuid(),
      displayName: z.string(),
      // Avatar initials, computed by the API from the AD name parts
      // (api/src/repositories/person.ts). The web renders this verbatim and
      // must never derive initials itself.
      initials: z.string(),
    }),
  ),
  totalMinutes: z.number(),
});
export type TaskDto = z.infer<typeof TaskDto>;

export const CreateTaskRequest = z.object({
  name: z.string().min(1).max(255),
  classificationId: z.string().uuid(),
});
export type CreateTaskRequest = z.infer<typeof CreateTaskRequest>;

export const PatchTaskRequest = z
  .object({
    name: z.string().min(1).max(255),
    classificationId: z.string().uuid(),
    activeFlag: z.boolean(),
  })
  .partial();
export type PatchTaskRequest = z.infer<typeof PatchTaskRequest>;

export const LockTaskRequest = z.object({
  lockedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type LockTaskRequest = z.infer<typeof LockTaskRequest>;

// ---- Assignments ------------------------------------------------------------

export const AssignmentSource = z.enum(['direct', 'team_fan_out']);
export type AssignmentSource = z.infer<typeof AssignmentSource>;

export const AssignmentDto = z.object({
  assignmentId: z.string().uuid(),
  taskId: z.string().uuid(),
  assigneeUserId: z.string().uuid(),
  source: AssignmentSource,
  activeFlag: z.boolean(),
  createdAt: z.string(),
});
export type AssignmentDto = z.infer<typeof AssignmentDto>;

export const CreateAssignmentRequest = z.object({
  userId: z.string().uuid(),
});
export type CreateAssignmentRequest = z.infer<typeof CreateAssignmentRequest>;

// ---- Task team memberships --------------------------------------------------

export const TaskTeamMembershipDto = z.object({
  taskTeamId: z.string().uuid(),
  taskId: z.string().uuid(),
  teamId: z.string().uuid(),
  createdAt: z.string(),
});
export type TaskTeamMembershipDto = z.infer<typeof TaskTeamMembershipDto>;

export const CreateTaskTeamMembershipRequest = z.object({
  teamId: z.string().uuid(),
});
export type CreateTaskTeamMembershipRequest = z.infer<typeof CreateTaskTeamMembershipRequest>;

// ---- Project team memberships -----------------------------------------------

export const ProjectTeamMembershipDto = z.object({
  projectTeamId: z.string().uuid(),
  projectId: z.string().uuid(),
  teamId: z.string().uuid(),
  createdAt: z.string(),
});
export type ProjectTeamMembershipDto = z.infer<typeof ProjectTeamMembershipDto>;

export const CreateProjectTeamMembershipRequest = z.object({
  teamId: z.string().uuid(),
});
export type CreateProjectTeamMembershipRequest = z.infer<typeof CreateProjectTeamMembershipRequest>;

// Project direct members were retired in Model B (slice S-B): membership is
// task-driven (leads + task-team members + task assignees). The
// project_direct_members table is orphaned for now; a follow-up migration drops it.

// ---- Members (derived view) -------------------------------------------------

export const MemberSourceKind = z.enum(['lead', 'team', 'direct']);
export type MemberSourceKind = z.infer<typeof MemberSourceKind>;

export const ProjectMemberDto = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  // Avatar initials, computed by the API from the AD name parts
  // (api/src/repositories/person.ts). The web renders this verbatim and
  // must never derive initials itself.
  initials: z.string(),
  sourceKind: MemberSourceKind,
  teamId: z.string().uuid().nullable(),
  onTaskIds: z.array(z.string().uuid()),
});
export type ProjectMemberDto = z.infer<typeof ProjectMemberDto>;
